import { Observable, Subscription, fromEvent, fromEventPattern } from 'rxjs';
import { map, concatAll, takeUntil, merge, tap, filter, pairwise, startWith } from 'rxjs/operators';
import fscreen from 'fscreen';

export const domReady = (func: () => void): void => {
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    setTimeout(func, 1);
  } else {
    document.addEventListener('DOMContentLoaded', func);
  }
}

export const markup2element = (markup: string, unwrap = true) => {
		let buildingSite = document.createElement('DIV');
		if (markup && markup.indexOf('NaN') !=-1){
			console.trace('NaN detected. markup=\n'+markup);
		}
		buildingSite.innerHTML = markup;
		return unwrap === false ? buildingSite : buildingSite.firstElementChild;
}

export const replaceNodeContent = (node: Node, content: Node, outerHTML = true): Node => {
  while (node.firstChild) {
    node.removeChild(node.lastChild);
  }
  if (content) {
    if (outerHTML) {
      node.appendChild(content);
    } else {
      while (content.firstChild) {
        node.appendChild(content.firstChild);
      }
    }
  }
  return node;
}

export let eventOptionsSupported: boolean = false;

try {
  let options = {
    get passive(): boolean { // This function will be called when the browser
      //   attempts to access the passive property.
      eventOptionsSupported = true;
      return true;
    },
    capture: false,
    once: false
  };

  let testHandler = function(event: Event) { };
  window.addEventListener("test", testHandler, options);
  window.removeEventListener("test", testHandler, options);
} catch (err) {
  eventOptionsSupported = false;
}
export const eventPatternObserver = (addHandler: (handler: any) => any, removeHandler?: (handler: any, signal?: any) => void): Observable<Event> => {
  return fromEventPattern(addHandler, removeHandler);
}

export const eventObserver = (elem: HTMLElement, eventType: string, eventOptions?: { passive?: boolean }): Observable<Event> => {
  let obs = fromEvent(elem, eventType, eventOptionsSupported ? eventOptions : undefined);
  return obs;
}
export interface DelegatedEvent{
	event: Event,
	target: HTMLElement
}
export const delegatedObserver = (top: HTMLElement, eventType: string, selector: string, eventOptions?: EventListenerOptions): Observable<DelegatedEvent> =>
  delegateObserver(top, fromEvent(top, eventType, eventOptionsSupported ? eventOptions : undefined), selector);

export const delegateObserver = (top: HTMLElement, topEventObservable: Observable<Event>, selector: string): Observable<DelegatedEvent> =>
  topEventObservable
    .pipe(
      map(event => { return { event: event, target: <HTMLElement>(<Element>event.target).closest(selector) }; }),
      filter(evAndTrgt => Array.from(top.querySelectorAll(selector)).includes(evAndTrgt.target))
    );

export const parent = (child: Element, selector: string): Element => {
  let closest = child ? child.closest(selector) : undefined;
  return child && child.parentNode === closest ? closest : undefined;
}

let debugCount = 0;
const touchEventToInfo = (evt: TouchEvent): string => {
  return evt.touches.length > 0 ?
    Array.from(evt.touches).map((touch, index) => {
      return ` ${debugCount++}  evt: ${evt.type} touch ${index}: id: ${touch.identifier}, screen: ( id: ${touch.pageX}, id: ${touch.pageY})`
    }).join('<br>')
    : ` ${debugCount++}  evt: ${evt.type} touch: NO TOUCHES`;

}

const showTouchDebugInfo = (touchEvent: TouchEvent, selector?: string) => {
  const touchDebugInfo = <HTMLElement>document.querySelector(selector ? selector : 'div.touchDebugInfo');
  if (touchDebugInfo) {
    touchDebugInfo.innerHTML = touchEventToInfo(touchEvent);
  }
}

const showPinchRotateInfo = (prInfo: { type: number, delta: { width: number, height: number, length: number, angle: number } }, selector?: string) => {
  const div = <HTMLElement>document.querySelector(selector ? selector : 'div.pinchRotateInfo');
  if (div) {
    div.innerHTML = `type:${prInfo.type}, delta:{ width:${prInfo.delta.width.toFixed(2)}, height:${prInfo.delta.height.toFixed(2)} , l:${prInfo.delta.length.toFixed(2)}, a:${(180.0 * (prInfo.delta.angle / Math.PI)).toFixed(0)} }`;
  }
}

const getTouchFromEvent = (touchEvent: TouchEvent, touchId: number): Touch => {
  if (touchId !== undefined) {
    const changedTouch = Array.from(touchEvent.changedTouches).filter(touch => touch.identifier == touchId).reduce((firstTouch, touch) => firstTouch ? firstTouch : touch, undefined);
    return changedTouch ? changedTouch : Array.from(touchEvent.touches).filter(touch => touch.identifier == touchId).reduce((firstTouch, touch) => firstTouch ? firstTouch : touch, undefined);
  }
  return undefined;
}

const createPinchRotateObserver = (startSelector: string, motionSelector?: string): Observable<{ type: number, start: { x: number, y: number }, delta: { width: number, height: number, length: number, angle: number } }> => {
  const startElement = <HTMLElement>document.querySelector(startSelector);
  const motionElement = motionSelector ? <HTMLElement>document.querySelector(motionSelector) : window;

  const touchStartObs = <Observable<TouchEvent>>fromEvent(startElement, 'touchstart', eventOptionsSupported ? { passive: false } : undefined);
  const touchMoveObs = <Observable<TouchEvent>>fromEvent(motionElement, 'touchmove', eventOptionsSupported ? { passive: true } : undefined);
  const touchEndObs = <Observable<TouchEvent>>fromEvent(motionElement, 'touchend');

  const pinchRotateStartObs = touchStartObs.pipe(filter(startEvt => startEvt.touches.length === 2), tap(event => event.preventDefault()));
  const pinchRotateEndObs = touchEndObs.pipe(merge(touchStartObs.pipe(filter(startEvt => startEvt.touches.length > 2))));

  let isFirst: boolean;
  let startPosition0: { touchId: number, x: number, y: number };
  let startPosition1: { touchId: number, x: number, y: number };
  let startVector: { cX: number, cY: number, width: number, height: number, length: number, angle: number };

  const toVector = (p0: { pageX: number, pageY: number }, p1: { pageX: number, pageY: number }) => {
    if (p0 && p1) {
      const x = p1.pageX - p0.pageX;
      const y = p1.pageY - p0.pageY;
      const l = Math.sqrt(x * x + y * y);
      let a = l === 0.0 ? undefined : Math.atan2(-y, x); //an undefined angle will not really happen with a touch device 
      return { cX: (p1.pageX + p0.pageX) / 2, cY: (p1.pageY + p0.pageY) / 2, width: Math.abs(x), height: Math.abs(y), length: l, angle: a };
    } else {
      return undefined;
    }
  }

  const eventToPinchRotateTouches = (event: TouchEvent): Touch[] => {
    return startPosition0 && startPosition1 && startVector ? [getTouchFromEvent(event, startPosition0.touchId), getTouchFromEvent(event, startPosition1.touchId)] : undefined;
  }

  const mapper = (event: { touches: Touch[], type: number }): { type: number, start: { x: number, y: number }, delta: { width: number, height: number, length: number, angle: number } } => {
    if (event && event.touches && event.touches.length == 2 && startVector) {
      const vector = toVector(event.touches[0], event.touches[1]);
      if (vector) {

        const angle = vector.angle != undefined && startVector.angle != undefined ? vector.angle - startVector.angle : 0.0;
        const length = startVector.length !== 0.0 ? vector.length / startVector.length : Number.POSITIVE_INFINITY;
        const width = startVector.width !== 0.0 ? vector.width / startVector.width : length;
        const height = startVector.height !== 0.0 ? vector.height / startVector.height : length;

        return { type: event.type, start: { x: startVector.cX, y: startVector.cY }, delta: { width: width, height: height, length: length, angle: angle > Math.PI ? angle - 2 * Math.PI : angle < -Math.PI ? angle + 2 * Math.PI : angle } };
      }
    }
    return undefined;
  };


  const pinhcRotateObs = <Observable<{ type: number, start: { x: number, y: number }, delta: { width: number, height: number, length: number, angle: number } }>>pinchRotateStartObs
    .pipe(
      map(event => {
        return touchMoveObs.pipe(
          map(moveEvt => eventToPinchRotateTouches(moveEvt)),
          map(pinchRotateTouches => {
            const type = isFirst ? 0 : 1;
            isFirst = false;
            return { touches: pinchRotateTouches, type: type };
          }),
          map(mapper),
          takeUntil(pinchRotateEndObs)
        );
      }),
      concatAll(),
      merge(
        pinchRotateStartObs.pipe(
          tap(touchEvent => {
            if (touchEvent.changedTouches.length == 2) {
              startPosition0 = { touchId: touchEvent.changedTouches[0].identifier, x: touchEvent.changedTouches[0].pageX, y: touchEvent.changedTouches[0].pageY };
              startPosition1 = { touchId: touchEvent.changedTouches[1].identifier, x: touchEvent.changedTouches[1].pageX, y: touchEvent.changedTouches[1].pageY };
              startVector = toVector(touchEvent.changedTouches[0], touchEvent.changedTouches[1]);
            } else {
              startPosition1 = { touchId: touchEvent.changedTouches[0].identifier, x: touchEvent.changedTouches[0].pageX, y: touchEvent.changedTouches[0].pageY };
              const touch0 = Array.from(touchEvent.touches).filter(touch => touch.identifier != startPosition1.touchId)[0];
              startPosition0 = { touchId: touch0.identifier, x: touch0.pageX, y: touch0.pageY };
              startVector = toVector(touch0, touchEvent.changedTouches[0]);
            }
            isFirst = true;
          }),
          map(touchEvent => {
            return { touches: eventToPinchRotateTouches(touchEvent), type: -1 };
          })
        ),
        pinchRotateEndObs.pipe(
          filter(touchEvent => startPosition0 !== undefined && startPosition1 !== undefined && startVector !== undefined),
          map(touchEvent => {
            return { touches: eventToPinchRotateTouches(touchEvent), type: isFirst ? -2 : 2 };
          }),
          map(mapper),
          tap(_ => { startPosition0 = undefined; startPosition1 = undefined; startVector = undefined })
        )
      ),
      filter(event => event.type >= 0)
    );
  return pinhcRotateObs;
}

const createTouchDragObserver = (startSelector: string, motionSelector?: string): Observable<{ type: number, start: { x: number, y: number }, delta: { x: number, y: number } }> => {
  const startElement = <HTMLElement>document.querySelector(startSelector);
  const motionElement = motionSelector ? <HTMLElement>document.querySelector(motionSelector) : window;

  const touchStartObs = <Observable<TouchEvent>>fromEvent(startElement, 'touchstart', eventOptionsSupported ? { passive: true } : undefined);
  const touchMoveObs = <Observable<TouchEvent>>fromEvent(motionElement, 'touchmove', eventOptionsSupported ? { passive: true } : undefined);
  const touchEndObs = <Observable<TouchEvent>>fromEvent(motionElement, 'touchend');

  const dragStartObs = touchStartObs.pipe(filter(startEvt => startEvt.touches.length === 1));
  const dragEndObs = touchEndObs.pipe(merge(touchStartObs.pipe(filter(startEvt => startEvt.touches.length > 1))));

  let isFirst: boolean;
  let startPosition: { touchId: number, x: number, y: number };

  const eventToDragTouch = (event: TouchEvent): Touch => {
    return startPosition ? getTouchFromEvent(event, startPosition.touchId) : undefined;
  }

  const mapper = (event: { touch: Touch, type: number }): { type: number, start: { x: number, y: number }, delta: { x: number, y: number } } => {
    return { type: event.type, start: { x: startPosition.x, y: startPosition.y }, delta: { x: (event.touch.pageX - startPosition.x), y: (event.touch.pageY - startPosition.y) } };
  };

  const dragObs = <Observable<{ type: number, start: { x: number, y: number }, delta: { x: number, y: number } }>>dragStartObs
    .pipe(
      map(event => {
        return touchMoveObs.pipe(
          map( (moveEvt: TouchEvent)  => eventToDragTouch(moveEvt)),
          map(dragTouch => {
            const type = isFirst ? 0 : 1;
            isFirst = false;
            return { touch: dragTouch, type: type };
          }),
          map(mapper),
          takeUntil(dragEndObs)
        );
      }),
      concatAll(),
      merge(
        dragStartObs.pipe(
          tap(touchEvent => {
            startPosition = { touchId: touchEvent.changedTouches[0].identifier, x: touchEvent.changedTouches[0].pageX, y: touchEvent.changedTouches[0].pageY };
            isFirst = true;
          }),
          map(touchEvent => { return { touch: eventToDragTouch(touchEvent), type: -1 }; })
        ),
        dragEndObs.pipe(
          filter(touchEvent => startPosition !== undefined),
          map(touchEvent => {
            return { touch: eventToDragTouch(touchEvent), type: isFirst ? -2 : 2 };
          }),
          map(mapper),
          tap(_ => { startPosition = undefined; })
        )
      )
    );
  return dragObs;

}

const createMouseDragObserver = (startSelector: string, motionSelector?: string): Observable<{ type: number, start: { x: number, y: number }, delta: { x: number, y: number } }> => {
  const startElement = <HTMLElement>document.querySelector(startSelector);
  const motionElement = motionSelector ? <HTMLElement>document.querySelector(motionSelector) : window;

  const mouseDownObs = <Observable<MouseEvent>>fromEvent(startElement, 'mousedown');
  const mouseUpObs = <Observable<MouseEvent>>fromEvent(motionElement, 'mouseup');
  const mouseMoveObs = <Observable<MouseEvent>>fromEvent(motionElement, 'mousemove');

  let isFirst: boolean;
  let startPosition: { x: number, y: number };

  let mapper = (event: { mouseEvent: MouseEvent, type: number }): { type: number, start: { x: number, y: number }, delta: { x: number, y: number } } => {
    return { type: event.type, start: { x: startPosition.x, y: startPosition.y }, delta: { x: (event.mouseEvent.pageX - startPosition.x), y: (event.mouseEvent.pageY - startPosition.y) } };
  };

  const dragObs = <Observable<{ type: number, start: { x: number, y: number }, delta: { x: number, y: number } }>>mouseDownObs
    .pipe(
      map(event => {
        event.preventDefault();//prevents unwanted text selection
        return mouseMoveObs.pipe(
          map(mouseEvent => {
            const type = isFirst ? 0 : 1;
            isFirst = false;
            return { mouseEvent: mouseEvent, type: type };
          }),
          map(mapper),
          takeUntil(mouseUpObs)
        );
      }),
      concatAll(),
      merge(
        mouseDownObs.pipe(
          tap(mouseEvent => {
            startPosition = { x: (<MouseEvent>event).pageX, y: (<MouseEvent>event).pageY };
            isFirst = true;
          }),
          map(mouseEvent => { return { mouseEvent: mouseEvent, type: -1 }; })
        ),
        mouseUpObs.pipe(
          filter(evt => startPosition !== undefined),
          map(mouseEvent => {
            return { mouseEvent: mouseEvent, type: isFirst ? -2 : 2 };
          }),
          map(mapper),
          tap(_ => { startPosition = undefined; })
        )
      )
    );
  return dragObs;

}




/**
 * A utility class that provides simple dragging of dom elements
 */
export class DragUtil {

  private container: Element;
  private dragSelector: string;
  private dragSubscription: Subscription;
  private pinchRotateSubscription: Subscription;

  /**
    * The container will hold all event listeners. It will also be the query select context for the 
    * other two selectors. The monitor node set will detect all drag gestures. All nodes that belong to the 
    * monitor set and NOT to the exclude set will actually move. So nodes selected by both the monitor set AND 
    * the exclude set will NOT move.
    * @param containerSelector the selector  for the container element
    * @param monitorSelector the selector for all elements that will listen for dragging behaviour and will be dragged
    * @param butExcludeTheseSelector selector for all nodes that fall in the monitor set but must not be dragged themselves
    */
  constructor(
    private containerSelector: string, //'#mainTimeline div.tlmd-tl .tlmd-tl-band'
    private monitorSelector: string, //'.tlmd-tl-layer, .tlmd-tl-cursor' 
    butExcludeTheseSelector: string,//'.tlmd-tl-static',
    private lockVertical: boolean,
    private lockHorizontal: boolean,
    private onDragStart: () => void,
    private onDragEnd: (dX: number, dY: number) => void,
    private onDrag: (dX: number, dY: number) => void,
    private onPinchRotateStart: () => void,
    private onPinchRotateEnd: (x: number, y: number, dW: number, dH: number, dL: number, dA: number) => void,
    private onPinchRotate: (x: number, y: number, dW: number, dH: number, dL: number, dA: number) => void,
    private onClick: (x: number, y: number) => void) {
    this.container = document.querySelectorAll(containerSelector).item(0);
    if (butExcludeTheseSelector) {
      this.dragSelector = '';
      let parts = monitorSelector.split(',');
      let part;
      for (let i = 0; i < parts.length; i++) {
        part = parts[i].trim();
        if (part.length > 0) {
          this.dragSelector += (i == 0 ? '' : ',') + part + ':not(' + butExcludeTheseSelector + ')';
        }
      }
    } else {
      this.dragSelector = monitorSelector;
    }

    this.init();
  }

  private init(): void {
    let self = this;

    let draggables: { elem: HTMLElement, x: number, y: number }[];
    const dragger = (dragState: { type: number, start: { x: number, y: number }, delta: { x: number, y: number }, dep: { elem: HTMLElement, x: number, y: number }[] }): void => {
      if (dragState.type === 0) {
        draggables = Array.from(this.container.querySelectorAll(this.dragSelector)).map(draggable => {
          return { elem: <HTMLElement>draggable, x: (<HTMLElement>draggable).offsetLeft, y: (<HTMLElement>draggable).offsetTop };
        });
        if (this.onDragStart) {
          this.onDragStart();
        }
      }
      if (dragState.type >= 0) {
        window.requestAnimationFrame(() => {
          draggables.forEach(draggable => {
            if (!this.lockHorizontal) draggable.elem.style.left = `${draggable.x + dragState.delta.x}px`;
            if (!this.lockVertical) draggable.elem.style.top = `${draggable.y + dragState.delta.y}px`;
          });
          if (this.onDrag) {
            this.onDrag(dragState.delta.x, dragState.delta.y);
          }
          if (dragState.type === 2 && this.onDragEnd) {
            this.onDragEnd(dragState.delta.x, dragState.delta.y);
          }
        });
      } else {
        if (dragState.type === -2 && this.onClick) {
          this.onClick(dragState.start.x, dragState.start.y);
        }
      }
    }

    this.dragSubscription = createMouseDragObserver(this.containerSelector)
      .pipe(
        merge(
          createTouchDragObserver(this.containerSelector)
        )
      )
      .subscribe(dragger);
    //.subscribe( dragger, (error) => { document.body.style.backgroundColor = '#ff0000';document.body.style.fontSize = '32pt';document.body.style.color = '#ffffff'; document.body.innerHTML = error; },  () => { document.body.style.backgroundColor = '#ff8000'; } );


    const pincherRotator = (pincRotateState: { type: number, start: { x: number, y: number }, delta: { width: number, height: number, length: number, angle: number } }): void => {
      if (pincRotateState.type === 0) {
        if (this.onPinchRotateStart) {
          this.onPinchRotateStart();
        }
      }
      window.requestAnimationFrame(() => {
        if (this.onPinchRotate) {
          this.onPinchRotate(pincRotateState.start.x, pincRotateState.start.y, pincRotateState.delta.width, pincRotateState.delta.height, pincRotateState.delta.length, pincRotateState.delta.angle);
        }
        if (pincRotateState.type === 2 && this.onPinchRotateEnd) {
          this.onPinchRotateEnd(pincRotateState.start.x, pincRotateState.start.y, pincRotateState.delta.width, pincRotateState.delta.height, pincRotateState.delta.length, pincRotateState.delta.angle);
        }
      });
      showPinchRotateInfo(pincRotateState);

    }

    this.pinchRotateSubscription = createPinchRotateObserver(this.containerSelector)
      .subscribe(pincherRotator);
    //.subscribe( pincherRotator, (error) => { document.body.style.backgroundColor = '#ff0000';document.body.style.fontSize = '32pt';document.body.style.color = '#ffffff'; document.body.innerHTML = error; },  () => { document.body.style.backgroundColor = '#ff8000'; } );
  }
}
export const hasFullscreen = () : boolean => {
    if(fscreen.fullscreenEnabled){
        window.document.body.classList.remove('nofullscreen');
    }else{
        window.document.body.classList.add('nofullscreen');
    }
    return fscreen.fullscreenEnabled;
}
export const requestFullscreen = () : void => {
    (<Promise<TypeError>><unknown>fscreen.requestFullscreen(window.document.body))
    .then( () => window.document.body.classList.add('fullscreen') , (error) => { 
                window.document.body.classList.remove('fullscreen'); 
                return error;
    })
    .catch( (error) => {
        window.document.body.classList.remove('fullscreen');
        return error;
    });
}
export const exitFullscreen = () => {
    fscreen.exitFullscreen();
    (<Promise<TypeError>><unknown>fscreen.requestFullscreen(window.document.body))
    .then( () => window.document.body.classList.remove('fullscreen') )
    .catch( (error) => {
        window.document.body.classList.add('fullscreen');
        return error;
    });
}
export const clearCookies = () => {
        var cookies = document.cookie.split("; ");
        for (let c = 0; c < cookies.length; c++) {
            let d = window.location.hostname.split(".");
            while (d.length > 0) {
                let cookieBase = encodeURIComponent(cookies[c].split(";")[0].split("=")[0]) + '=; expires=Thu, 01-Jan-1970 00:00:01 GMT; domain=' + d.join('.') + '; secure; sameSite=strict; path=';
                let p = location.pathname.split('/');
                document.cookie = cookieBase + '/';
                while (p.length > 0) {
                    document.cookie = cookieBase + p.join('/');
                    p.pop();
                };
                d.shift();
            }
        }
}
